import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export interface GroupedListGroup<T> {
  key: string;
  label: string;
  items: T[];
  className?: string;
}

export interface GroupedListProps<T> {
  groups: GroupedListGroup<T>[];
  renderItem: (item: T) => ReactNode;
  renderGroupHeader?: (group: GroupedListGroup<T>) => ReactNode;
  emptyMessage?: string;
  className?: string;
}

export const GroupedList = <T,>({
  groups,
  renderItem,
  renderGroupHeader,
  emptyMessage = 'Нет элементов',
  className = '',
}: GroupedListProps<T>) => {
  const hasItems = groups.some((group) => group.items.length > 0);

  if (!hasItems) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  const defaultRenderGroupHeader = (group: GroupedListGroup<T>) => (
    <h2 className="text-xl font-normal text-gray-500 leading-4">
      {group.label}
    </h2>
  );

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {groups.map((group) => {
        if (group.items.length === 0) {
          return null;
        }

        return (
          <div key={group.key} className="flex flex-col gap-4">
            {renderGroupHeader
              ? renderGroupHeader(group)
              : defaultRenderGroupHeader(group)}
            <div className={twMerge('flex flex-col gap-4', group.className)}>
              {group.items.map((item, index) => (
                <div key={index}>{renderItem(item)}</div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
